import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  Bars3BottomLeftIcon,
  FlagIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

import { CheckTypeInfo } from "../api/Blueprints";
import { useGetCheckTypes, usePostBlueprintTask } from "../api/BlueprintsApi";
import { REPORT_TYPES } from "../api/Reports";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  FilterSortHeader,
  FilterValues,
  TriStateFilterValue,
} from "../360/FilterSortHeader";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import Container from "../shared-components/Container";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import PageHeader from "../shared-components/PageHeader";
import PaginationInMemory from "../shared-components/PaginationInMemory";
import BehaviorFlagBadge from "../shared-components/BehaviorFlagBadge";
import {
  classNames,
  getBackgroundColorFromCheckType,
  sortNamedListAlpha,
} from "../utils";
import { behaviorRelabelMapping, useFamaFlags } from "../api/FamaFlags";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import { PopMenu } from "../shared-components/PopMenu";
import ElipseIcon from "../shared-components/icons/ElipseIcon";
import { HoverExpandCell } from "../shared-components/HoverExpandCell";
import { ItemTypeBadge, ItemTypesCell } from "./itemTypeIcons";
import { SourceBadge, SourceCell, type SourceDisplayMode } from "./sourceIcons";
import {
  CHECK_TYPES_ICON_BADGE_TOOLTIP_ID,
  IconBadgeTooltipLayer,
  IconBadgeTooltipProvider,
  useRebuildIconBadgeTooltips,
} from "./iconBadgeTooltip";
import { renderReportMenuItem } from "../reports/renderReportMenuItem";
import { checkTypeSourceNames } from "./checkTypeSources";
import { slaDisplay } from "./slaDisplay";

const PAGE_SIZE = 20;
const COLLAPSED_COLUMN_WIDTH = "4rem";

type CheckTypeColumnKey =
  | "name"
  | "description"
  | "sources"
  | "itemTypes"
  | "behaviors"
  | "slaLookback"
  | "workflowColor";

type HiddenColumns = Record<CheckTypeColumnKey, boolean>;

const DEFAULT_HIDDEN_COLUMNS: HiddenColumns = {
  name: false,
  description: false,
  sources: false,
  itemTypes: false,
  behaviors: true,
  slaLookback: false,
  workflowColor: true,
};

function checkTypesGridTemplateColumns(hiddenColumns: HiddenColumns): string {
  return [
    hiddenColumns.name ? COLLAPSED_COLUMN_WIDTH : "1.4fr",
    hiddenColumns.description ? COLLAPSED_COLUMN_WIDTH : "1fr",
    hiddenColumns.sources ? COLLAPSED_COLUMN_WIDTH : "1fr",
    hiddenColumns.itemTypes ? COLLAPSED_COLUMN_WIDTH : "1fr",
    hiddenColumns.behaviors ? COLLAPSED_COLUMN_WIDTH : "1.5fr",
    hiddenColumns.slaLookback ? COLLAPSED_COLUMN_WIDTH : "1fr",
    hiddenColumns.workflowColor ? COLLAPSED_COLUMN_WIDTH : "1fr",
    "3rem",
  ].join(" ");
}

type CheckTypeRow = CheckTypeInfo & {
  row_id: string;
  name_display: string;
  color_display: string;
  description_display: string;
  report_type_display: string;
  workflow_name_display: string;
  source_names: string;
  behavior_names: string;
  sla_type: string;
  lookback_display: string;
};

function reportTypeDisplay(reportType?: string): string {
  if (reportType === REPORT_TYPES.THREE_SIXTY) return "360";
  return "Misconduct";
}

function sourceNames(sources?: string[]): string[] {
  return checkTypeSourceNames(sources);
}

function activeBehaviorNames(behaviors?: string[]): string[] {
  return sortNamedListAlpha(Array.from(new Set(behaviors ?? [])));
}

function itemTypeNames(itemTypes?: string[]): string[] {
  return sortNamedListAlpha(Array.from(new Set(itemTypes ?? [])));
}

function buildRows(checkTypes: CheckTypeInfo[] | undefined): CheckTypeRow[] {
  return (checkTypes ?? []).map((checkType, index) => {
    const activeSources = sourceNames(checkType.sources);
    const behaviorNames = activeBehaviorNames(checkType.behaviors);
    return {
      ...checkType,
      sources: activeSources,
      row_id:
        checkType.blueprint_uuid ??
        `${checkType.name ?? "check-type"}-${index}`,
      name_display: checkType.name ?? "",
      color_display: checkType.color || "Default",
      description_display: checkType.description || "",
      report_type_display: reportTypeDisplay(checkType.report_type),
      workflow_name_display: checkType.workflow_version
        ? `${checkType.workflow_name ?? "NA"} v${checkType.workflow_version}`
        : checkType.workflow_name ?? "NA",
      source_names: activeSources.join(", "),
      behavior_names: behaviorNames.join(", "),
      sla_type: slaDisplay(checkType.turn_around, checkType.sla_hours),
      lookback_display: `${checkType.lookback} months`,
    };
  });
}

function rowSortValue(row: CheckTypeRow, field: string): string | number {
  switch (field) {
    case "sources":
      return sourceNames(row.sources).join(", ");
    case "behaviors":
      return activeBehaviorNames(row.behaviors).join(", ");
    case "item_types":
      return itemTypeNames(row.item_types).join(", ");
    default: {
      const value = row[field as keyof CheckTypeRow];
      if (typeof value === "number") {
        return value;
      }
      return String(value ?? "");
    }
  }
}

function compareRows(field: string, reverse: boolean) {
  return (a: CheckTypeRow, b: CheckTypeRow): number => {
    const aValue = rowSortValue(a, field);
    const bValue = rowSortValue(b, field);
    const result =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue));
    return reverse ? -result : result;
  };
}

function filterRows(
  rows: CheckTypeRow[],
  activeFilters: FilterValues[]
): CheckTypeRow[] {
  return activeFilters.reduce((filteredRows, activeFilter) => {
    if (
      activeFilter.field === "sources" ||
      activeFilter.field === "behaviors" ||
      activeFilter.field === "item_types"
    ) {
      const triStateValues = activeFilter.values as TriStateFilterValue[];
      const included = triStateValues
        .filter((value) => value.state === "include")
        .map((value) => value.value);
      const excluded = triStateValues
        .filter((value) => value.state === "exclude")
        .map((value) => value.value);
      return filteredRows.filter((row) => {
        const values =
          activeFilter.field === "sources"
            ? sourceNames(row.sources)
            : activeFilter.field === "behaviors"
            ? activeBehaviorNames(row.behaviors)
            : itemTypeNames(row.item_types);
        return (
          included.every((value) => values.includes(value)) &&
          excluded.every((value) => !values.includes(value))
        );
      });
    }
    return filteredRows.filter((row) =>
      activeFilter.values.includes(
        row[activeFilter.field as keyof CheckTypeRow]
      )
    );
  }, rows);
}

function BehaviorCountBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-gray-700">
      {count}
      <FlagIcon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

function BehaviorFlagsCell({ behaviors }: { behaviors?: string[] }) {
  const { behaviorMappingActions } = useFamaFlags();
  const names = activeBehaviorNames(behaviors);
  const relabeledNames = behaviorRelabelMapping(names, behaviorMappingActions);

  if (relabeledNames.length === 0) {
    return <span className="text-gray-400">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {relabeledNames.map((name) => (
        <BehaviorFlagBadge key={name} name={name} />
      ))}
    </div>
  );
}

function CheckTypeColorSwatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      title={color}
      className={classNames(
        "inline-block h-4 w-4 rounded-sm border border-black",
        getBackgroundColorFromCheckType(color)
      )}
    />
  );
}

function CheckTypeColorDisplay({ color }: { color: string }) {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <CheckTypeColorSwatch color={color} />
      <span>{color}</span>
    </div>
  );
}

function UpdateTasksAction({ row }: { row: CheckTypeRow }) {
  const { showLargeToast } = useApplicationToast();
  const {
    isLoading: isTaskLoading,
    isError: isTaskError,
    mutate: postBlueprintTask,
    data: taskData,
  } = usePostBlueprintTask();

  useEffect(() => {
    if (isTaskError) {
      showLargeToast?.({ message: "Error updating task", title: "Error" });
    }
    if (taskData) {
      showLargeToast?.({ message: "Task updated", title: "Success" });
    }
  }, [isTaskError, taskData, showLargeToast]);

  if (isTaskLoading) {
    return <LoadingSpinner />;
  }

  return (
    <PopMenu
      button={<ElipseIcon />}
      menuItems={[
        {
          component: renderReportMenuItem(
            ArrowPathRoundedSquareIcon,
            "Update Tasks",
            "text-sm"
          ),
          onClick: () =>
            postBlueprintTask({
              taskDisplay: row.name ?? "",
              taskKey: row.blueprint_uuid ?? "",
            }),
        },
      ]}
    />
  );
}

function CheckTypes() {
  const navigate = useNavigate();
  const { canAddBlueprints, canManageBlueprints } = usePermissionValidator();
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState("name_display");
  const [reverse, setReverse] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterValues[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<HiddenColumns>(
    DEFAULT_HIDDEN_COLUMNS
  );
  const [sourcesDisplayMode, setSourcesDisplayMode] =
    useState<SourceDisplayMode>("text");
  const {
    data: checkTypes,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetCheckTypes({ enabled: canManageBlueprints });

  const rows = useMemo(() => buildRows(checkTypes), [checkTypes]);
  const filteredRows = useMemo(
    () => filterRows(rows, activeFilters),
    [rows, activeFilters]
  );
  const sortedRows = useMemo(
    () => [...filteredRows].sort(compareRows(sortField, reverse)),
    [filteredRows, sortField, reverse]
  );
  const activeRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [page, sortedRows]
  );
  const gridTemplateColumns = useMemo(
    () => checkTypesGridTemplateColumns(hiddenColumns),
    [hiddenColumns]
  );

  useRebuildIconBadgeTooltips([
    activeRows,
    page,
    activeFilters,
    hiddenColumns,
    sourcesDisplayMode,
  ]);

  function setColumnHidden(column: CheckTypeColumnKey, hidden: boolean) {
    setHiddenColumns((current) => ({ ...current, [column]: hidden }));
  }

  function setSortBy(field: string) {
    if (!field) return;
    setPage(1);
    if (field === sortField) {
      setReverse(!reverse);
    } else {
      setSortField(field);
      setReverse(false);
    }
  }

  function setFilters(field: string, values: any[]) {
    setPage(1);
    const newFilters = activeFilters.filter((filter) => filter.field !== field);
    if (values.length > 0) {
      newFilters.push({ field, values });
    }
    setActiveFilters(newFilters);
  }

  if (!canManageBlueprints) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <>
      <PageHeader title="Check Types">
        <div className="flex items-center gap-4">
          <ArrowPathIcon
            className="w-5 stroke-green-500 cursor-pointer"
            onClick={() => {
              setPage(1);
              refetch();
            }}
          />
          {canAddBlueprints && (
            <Button
              type={ButtonTypes.add}
              text="Add New Check Type"
              colorType={ButtonColorTypes.success}
              onClick={() => navigate("/check-types/new")}
            />
          )}
        </div>
      </PageHeader>
      <IconBadgeTooltipProvider tooltipId={CHECK_TYPES_ICON_BADGE_TOOLTIP_ID}>
        <Container className="overflow-x-auto">
          {isError && (
            <ErrorMessageContainer message="Cannot get check type information, please contact support" />
          )}
          <div className="min-w-[1080px]">
            <div
              className="grid gap-2 text-gray-500 text-xs font-medium bg-gray-50 p-2 border rounded-t-xl border-gray-300 uppercase"
              style={{ gridTemplateColumns }}
            >
              <FilterSortHeader
                headers={[
                  {
                    display: "Name",
                    value_field: "name_display",
                    filterable: true,
                    sortable: true,
                    searchable: true,
                  },
                  {
                    display: "Report Type",
                    value_field: "report_type_display",
                    filterable: true,
                    sortable: true,
                  },
                ]}
                sortField={sortField}
                reverse={reverse}
                sortCallback={setSortBy}
                data={rows}
                activeFilters={activeFilters}
                setFilterCallback={setFilters}
                hidden={hiddenColumns.name}
                onHiddenChange={(hidden) => setColumnHidden("name", hidden)}
              />
              <FilterSortHeader
                headers={[
                  {
                    display: "Description",
                    value_field: "description_display",
                    filterable: true,
                    sortable: true,
                    searchable: true,
                    hideable: true,
                  },
                ]}
                sortField={sortField}
                reverse={reverse}
                sortCallback={setSortBy}
                data={rows}
                activeFilters={activeFilters}
                setFilterCallback={setFilters}
                hidden={hiddenColumns.description}
                onHiddenChange={(hidden) =>
                  setColumnHidden("description", hidden)
                }
              />
              <FilterSortHeader
                headers={[
                  {
                    display: "Sources",
                    value_field: "sources",
                    filterable: true,
                    sortable: true,
                    filterVariant: "listTriState",
                    hideable: true,
                    renderFilterOption: (value) => (
                      <SourceBadge sourceName={value} />
                    ),
                  },
                ]}
                sortField={sortField}
                reverse={reverse}
                sortCallback={setSortBy}
                data={rows}
                activeFilters={activeFilters}
                setFilterCallback={setFilters}
                hidden={hiddenColumns.sources}
                onHiddenChange={(hidden) => setColumnHidden("sources", hidden)}
                headerAccessory={
                  <button
                    type="button"
                    aria-label={
                      sourcesDisplayMode === "icon"
                        ? "Show source names"
                        : "Show source icons"
                    }
                    title={
                      sourcesDisplayMode === "icon"
                        ? "Show source names"
                        : "Show source icons"
                    }
                    className="flex text-gray-400 hover:text-gray-600"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSourcesDisplayMode((current) =>
                        current === "icon" ? "text" : "icon"
                      );
                    }}
                  >
                    {sourcesDisplayMode === "icon" ? (
                      <Bars3BottomLeftIcon className="h-4 w-4" />
                    ) : (
                      <Squares2X2Icon className="h-4 w-4" />
                    )}
                  </button>
                }
              />
              <FilterSortHeader
                headers={[
                  {
                    display: "Item Types",
                    value_field: "item_types",
                    filterable: true,
                    sortable: true,
                    filterVariant: "listTriState",
                    hideable: true,
                    renderFilterOption: (value) => (
                      <ItemTypeBadge itemTypeName={value} />
                    ),
                  },
                ]}
                sortField={sortField}
                reverse={reverse}
                sortCallback={setSortBy}
                data={rows}
                activeFilters={activeFilters}
                setFilterCallback={setFilters}
                hidden={hiddenColumns.itemTypes}
                onHiddenChange={(hidden) =>
                  setColumnHidden("itemTypes", hidden)
                }
              />
              <FilterSortHeader
                headers={[
                  {
                    display: "Behavior Flags",
                    value_field: "behaviors",
                    filterable: true,
                    sortable: true,
                    filterVariant: "listTriState",
                    hideable: true,
                  },
                ]}
                sortField={sortField}
                reverse={reverse}
                sortCallback={setSortBy}
                data={rows}
                activeFilters={activeFilters}
                setFilterCallback={setFilters}
                hidden={hiddenColumns.behaviors}
                onHiddenChange={(hidden) =>
                  setColumnHidden("behaviors", hidden)
                }
              />
              <FilterSortHeader
                headers={[
                  {
                    display: "Lookback",
                    value_field: "lookback",
                    filterable: true,
                    sortable: true,
                  },
                  {
                    display: "SLA Type",
                    value_field: "sla_type",
                    filterable: true,
                    sortable: true,
                  },
                ]}
                sortField={sortField}
                reverse={reverse}
                sortCallback={setSortBy}
                data={rows}
                activeFilters={activeFilters}
                setFilterCallback={setFilters}
                hidden={hiddenColumns.slaLookback}
                onHiddenChange={(hidden) =>
                  setColumnHidden("slaLookback", hidden)
                }
              />
              <FilterSortHeader
                headers={[
                  {
                    display: "Color",
                    value_field: "color_display",
                    filterable: true,
                    sortable: true,
                    hideable: true,
                  },
                  {
                    display: "Workflow",
                    value_field: "workflow_name_display",
                    filterable: true,
                    sortable: true,
                    searchable: true,
                  },
                ]}
                sortField={sortField}
                reverse={reverse}
                sortCallback={setSortBy}
                data={rows}
                activeFilters={activeFilters}
                setFilterCallback={setFilters}
                hidden={hiddenColumns.workflowColor}
                onHiddenChange={(hidden) =>
                  setColumnHidden("workflowColor", hidden)
                }
              />
              <div className="text-md font-bold text-gray-900">Actions</div>
            </div>
            <div className="bg-white border rounded-b-xl border-gray-300 text-xs">
              {isLoading && activeRows.length === 0 ? (
                <div className="p-4">
                  <LoadingSpinner />
                </div>
              ) : activeRows.length === 0 ? (
                <div className="p-4">no data found</div>
              ) : (
                <>
                  {activeRows.map((row) => (
                    <div
                      key={row.row_id}
                      className="grid gap-2 border-b border-gray-100 p-2 last:border-b-0"
                      style={{ gridTemplateColumns }}
                    >
                      <div>
                        {!hiddenColumns.name && (
                          <div className="space-y-1">
                            <div className="font-medium text-gray-900">
                              {row.name_display}
                            </div>
                            <div className="text-xs text-gray-500">
                              {row.report_type_display}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <HoverExpandCell
                          collapsed={hiddenColumns.description}
                          collapsedContent="···"
                          expandedContent={
                            <div className="text-xs text-gray-600">
                              {row.description_display || "No description"}
                            </div>
                          }
                        />
                      </div>
                      <div>
                        <HoverExpandCell
                          collapsed={hiddenColumns.sources}
                          collapsedContent={sourceNames(row.sources).length}
                          className={hiddenColumns.sources ? "text-center" : ""}
                          expandedContent={
                            <SourceCell
                              rowSourceNames={sourceNames(row.sources)}
                              displayMode={sourcesDisplayMode}
                            />
                          }
                        />
                      </div>
                      <div>
                        <HoverExpandCell
                          collapsed={hiddenColumns.itemTypes}
                          collapsedContent={
                            itemTypeNames(row.item_types).length
                          }
                          className={
                            hiddenColumns.itemTypes ? "text-center" : ""
                          }
                          expandedContent={
                            <ItemTypesCell itemTypes={row.item_types} />
                          }
                        />
                      </div>
                      <div>
                        <HoverExpandCell
                          collapsed={hiddenColumns.behaviors}
                          collapsedContent={
                            <BehaviorCountBadge
                              count={activeBehaviorNames(row.behaviors).length}
                            />
                          }
                          className={
                            hiddenColumns.behaviors ? "text-center" : ""
                          }
                          expandPanelClassName="!min-w-[200px] !max-w-[300px]"
                          expandedContent={
                            <BehaviorFlagsCell behaviors={row.behaviors} />
                          }
                        />
                      </div>
                      <div>
                        {!hiddenColumns.slaLookback && (
                          <div className="space-y-1">
                            <div>{row.lookback_display}</div>
                            <div className="text-xs text-gray-500">
                              {row.sla_type}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <HoverExpandCell
                          collapsed={hiddenColumns.workflowColor}
                          collapsedContent={
                            <CheckTypeColorSwatch color={row.color_display} />
                          }
                          className={
                            hiddenColumns.workflowColor
                              ? "flex justify-center"
                              : ""
                          }
                          expandedContent={
                            <div className="space-y-1">
                              <CheckTypeColorDisplay
                                color={row.color_display}
                              />
                              <div>{row.workflow_name_display}</div>
                            </div>
                          }
                        />
                      </div>
                      <div className="flex justify-center">
                        <UpdateTasksAction row={row} />
                      </div>
                    </div>
                  ))}
                  {isFetching && rows.length > 0 && (
                    <div className="p-2 text-gray-400">
                      {isLoading
                        ? "Loading check types..."
                        : "Refreshing check types..."}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {rows.length > 0 && (
            <PaginationInMemory
              currentPage={page}
              pageSize={PAGE_SIZE}
              totalCount={filteredRows.length}
              unfilteredCount={rows.length}
              clearFilters={() => {
                setActiveFilters([]);
                setPage(1);
              }}
              pageChange={(idx: number) => setPage(idx)}
              showDescription={true}
              activeStyle="bg-green-50"
            />
          )}
        </Container>
      </IconBadgeTooltipProvider>
      <IconBadgeTooltipLayer id={CHECK_TYPES_ICON_BADGE_TOOLTIP_ID} />
    </>
  );
}

export default CheckTypes;
